import { useEffect, useRef, useState } from "react";

export type HubEvent =
  | { type: "listing.update"; saved_search_id: number; data: unknown }
  | { type: "scan.start"; saved_search_id: number; data: unknown }
  | { type: "scan.finish"; saved_search_id: number; data: unknown }
  | { type: "alert.new"; saved_search_id: number; data: unknown }
  | { type: "status"; saved_search_id: number; data: unknown };

export type WSStatus = "connecting" | "open" | "closed";

export function useWS(args: {
  savedSearchId: number | "all";
  onEvent: (e: HubEvent) => void;
  enabled?: boolean;
}): { status: WSStatus } {
  const [status, setStatus] = useState<WSStatus>("connecting");
  const onEventRef = useRef(args.onEvent);
  onEventRef.current = args.onEvent;

  useEffect(() => {
    if (args.enabled === false) {
      setStatus("closed");
      return;
    }

    let attempts = 0;
    let alive = true;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;

    function open() {
      if (!alive) return;
      setStatus("connecting");
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const url = `${proto}://${window.location.host}/api/ws?savedSearchId=${args.savedSearchId}`;
      socket = new WebSocket(url);
      socket.addEventListener("open", () => {
        if (!alive) return;
        attempts = 0;
        setStatus("open");
      });
      socket.addEventListener("message", (e) => {
        if (!alive) return;
        try {
          const parsed = JSON.parse(e.data) as HubEvent;
          onEventRef.current(parsed);
        } catch {
          /* drop malformed */
        }
      });
      socket.addEventListener("close", () => {
        if (!alive) return;
        setStatus("closed");
        attempts += 1;
        const delay = Math.min(30_000, 500 * 2 ** Math.min(attempts, 6));
        reconnectTimer = window.setTimeout(open, delay);
      });
    }

    open();
    return () => {
      alive = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) socket.close();
    };
  }, [args.savedSearchId, args.enabled]);

  return { status };
}
